import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { buildConsoleRequestHeaders } from '../utils/backendRequest';
import { ClipLoader } from 'react-spinners';

const LABEL_ZOOM_MIN = 0.5;
const LABEL_ZOOM_MAX = 3;
const LABEL_ZOOM_STEP = 0.25;

function deliveryCoreBaseUrl() {
  return process.env.REACT_APP_DELIVERY_CORE_API_URL || '';
}

function deliveryCoreUrl(path) {
  const base = deliveryCoreBaseUrl().replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  // When env is unset, use same-origin relative URLs so setupProxy can forward them.
  if (base) {
    return `${base}${normalized}`;
  }
  return normalized;
}

function ordersRequestHeaders(token, user) {
  return buildConsoleRequestHeaders(token, user);
}

function imageToDataUrl(image) {
  const data = String(image?.data || '').trim();
  if (!data) return null;
  if (data.startsWith('data:')) return data;
  const contentType = image?.content_type || 'image/png';
  return `data:${contentType};base64,${data}`;
}

function isPdfLabel(image, dataUrl) {
  return (image?.content_type || '').includes('pdf') || dataUrl.startsWith('data:application/pdf');
}

function printLabelImage(image) {
  const dataUrl = imageToDataUrl(image);
  if (!dataUrl) return;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);

  const cleanup = () => {
    window.setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 1000);
  };

  const triggerPrint = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (err) {
      console.error('Print failed:', err);
    } finally {
      cleanup();
    }
  };

  if (isPdfLabel(image, dataUrl)) {
    iframe.onload = triggerPrint;
    iframe.src = dataUrl;
    return;
  }

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    cleanup();
    return;
  }

  doc.open();
  doc.write(
    `<!DOCTYPE html><html><head><title>Print label</title>
    <style>
      @page { margin: 0; }
      html, body { margin: 0; padding: 0; }
      img { max-width: 100%; height: auto; display: block; margin: 0 auto; }
    </style>
    </head><body><img src="${dataUrl}" alt="Label" /></body></html>`,
  );
  doc.close();

  const img = doc.querySelector('img');
  if (!img) {
    cleanup();
    return;
  }

  if (img.complete) {
    triggerPrint();
  } else {
    img.onload = triggerPrint;
    img.onerror = cleanup;
  }
}

function LabelImagePage({ image, caption, zoom }) {
  const dataUrl = imageToDataUrl(image);
  if (!dataUrl) return null;

  const isPdf = isPdfLabel(image, dataUrl);

  return (
    <div className="webhooks-labelImageFrame">
      <div className="webhooks-labelImageViewport">
        {isPdf ? (
          <iframe
            title={caption}
            src={dataUrl}
            className="webhooks-labelImagePdf"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
          />
        ) : (
          <img
            src={dataUrl}
            alt={caption}
            className="webhooks-labelImageImg"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
          />
        )}
      </div>
      <div className="webhooks-labelImageCaption">{caption}</div>
    </div>
  );
}

function LabelImageCarousel({ images, theme, t }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    setActiveIndex(0);
    setZoom(1);
  }, [images]);

  useEffect(() => {
    setZoom(1);
  }, [activeIndex]);

  if (!images.length) {
    return null;
  }

  const total = images.length;
  const safeIndex = Math.min(activeIndex, total - 1);
  const current = images[safeIndex];
  const pageNumber = safeIndex + 1;
  const caption = t('shipmentEditorLabelPage', { n: pageNumber });

  return (
    <div className="webhooks-labelCarousel">
      <div className="webhooks-labelCarouselToolbar">
        <button
          type="button"
          className={['webhooks-iconBtn', 'webhooks-labelCarouselBtn', theme].join(' ')}
          onClick={() => setActiveIndex((index) => Math.max(0, index - 1))}
          disabled={safeIndex === 0}
          aria-label={t('Prev')}
          title={t('Prev')}
        >
          <span className="oi oi-chevron-left" aria-hidden="true" />
        </button>
        <div className="webhooks-labelCarouselCounter">
          {caption}
          <span className="webhooks-labelCarouselCounterSep">
            {pageNumber} / {total}
          </span>
        </div>
        <button
          type="button"
          className={['webhooks-iconBtn', 'webhooks-labelCarouselBtn', theme].join(' ')}
          onClick={() => setActiveIndex((index) => Math.min(total - 1, index + 1))}
          disabled={safeIndex >= total - 1}
          aria-label={t('Next')}
          title={t('Next')}
        >
          <span className="oi oi-chevron-right" aria-hidden="true" />
        </button>
      </div>

      <div className="webhooks-labelCarouselActions">
        <button
          type="button"
          className={['webhooks-secondaryBtn', 'webhooks-labelCarouselPrintBtn', theme, 'enabled'].join(' ')}
          onClick={() => printLabelImage(current)}
          aria-label={t('shipmentEditorPrintLabel')}
          title={t('shipmentEditorPrintLabel')}
        >
          <span className="oi oi-print" aria-hidden="true" />
          <span>{t('shipmentEditorPrintLabel')}</span>
        </button>
        <button
          type="button"
          className={[
            'webhooks-secondaryBtn',
            'webhooks-labelCarouselZoomBtn',
            theme,
            zoom <= LABEL_ZOOM_MIN ? 'disabled' : 'enabled',
          ].join(' ')}
          onClick={() => setZoom((value) => Math.max(LABEL_ZOOM_MIN, +(value - LABEL_ZOOM_STEP).toFixed(2)))}
          disabled={zoom <= LABEL_ZOOM_MIN}
          aria-label={t('shipmentEditorZoomOut')}
          title={t('shipmentEditorZoomOut')}
        >
          <span className="oi oi-zoom-out" aria-hidden="true" />
        </button>
        <button
          type="button"
          className={[
            'webhooks-secondaryBtn',
            'webhooks-labelCarouselZoomBtn',
            theme,
            zoom >= LABEL_ZOOM_MAX ? 'disabled' : 'enabled',
          ].join(' ')}
          onClick={() => setZoom((value) => Math.min(LABEL_ZOOM_MAX, +(value + LABEL_ZOOM_STEP).toFixed(2)))}
          disabled={zoom >= LABEL_ZOOM_MAX}
          aria-label={t('shipmentEditorZoomIn')}
          title={t('shipmentEditorZoomIn')}
        >
          <span className="oi oi-zoom-in" aria-hidden="true" />
        </button>
      </div>

      <LabelImagePage key={safeIndex} image={current} caption={caption} zoom={zoom} />

      {total > 1 && (
        <div className="webhooks-labelCarouselDots" role="tablist" aria-label={t('shipmentEditorLabelImages')}>
          {images.map((image, index) => (
            <button
              key={image?.page ?? index}
              type="button"
              role="tab"
              aria-selected={index === safeIndex}
              aria-label={t('shipmentEditorLabelPage', { n: index + 1 })}
              className={[
                'webhooks-labelCarouselDot',
                index === safeIndex ? 'is-active' : '',
                theme,
              ].filter(Boolean).join(' ')}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function formatTs(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function formatReferences(refs) {
  if (!Array.isArray(refs) || refs.length === 0) return '—';
  return refs.map((ref) => String(ref)).join(', ');
}

function formatBoxDimensions(box) {
  if (!box || typeof box !== 'object') return '—';

  const dims = [box.length_cm, box.width_cm, box.height_cm].map((value) => {
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : '—';
  });

  const weightN = Number(box.weight_kg);
  const weightStr = Number.isFinite(weightN) ? weightN.toFixed(2) : null;
  if (weightStr) return `${dims.join('x')}, ${weightStr}`;
  return dims.join('x');
}

async function copyTextToClipboard(text) {
  const value = String(text || '');
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = value;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.top = '-9999px';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return !!ok;
    } catch {
      return false;
    }
  }
}

function ShipmentBoxesTable({ shipment, t }) {
  const planned = Array.isArray(shipment?.shipment_boxes_planned) ? shipment.shipment_boxes_planned : [];
  const actual = Array.isArray(shipment?.shipment_boxes_actual) ? shipment.shipment_boxes_actual : [];
  const rows = [
    ...planned.map((box) => ({ box, kind: 'planned' })),
    ...actual.map((box) => ({ box, kind: 'actual' })),
  ];

  if (rows.length === 0) {
    return <div className="webhooks-muted">{t('shipmentEditorBoxesNone')}</div>;
  }

  return (
    <div className="webhooks-tableWrap">
      <table className="webhooks-table">
        <thead>
          <tr>
            <th className="webhooks-th">{t('shipmentEditorBoxKind')}</th>
            <th className="webhooks-th">{t('shipmentEditorBoxCode')}</th>
            <th className="webhooks-th">{t('shipmentEditorBoxQty')}</th>
            <th className="webhooks-th">{t('ordersDetailsCalcParcels')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ box, kind }, idx) => (
            <tr
              key={box?.id || `${kind}-${idx}`}
              className={idx === rows.length - 1 ? 'webhooks-trLast' : undefined}
            >
              <td className="webhooks-td">
                {kind === 'planned' ? t('shipmentEditorBoxPlanned') : t('shipmentEditorBoxActual')}
              </td>
              <td className="webhooks-td">{String(box?.box_code || '—')}</td>
              <td className="webhooks-td">{box?.qty ?? '—'}</td>
              <td className="webhooks-td">{formatBoxDimensions(box)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FieldValue({ children, preWrap = false }) {
  return (
    <div className={['webhooks-fieldValue', preWrap ? 'is-preWrap' : ''].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

function MultilineText({ value }) {
  const text = value != null && String(value).trim() !== '' ? String(value) : '—';
  return <FieldValue preWrap>{text}</FieldValue>;
}

function labelFieldsPayload(shipment) {
  const labelFields = shipment?.label_fields;
  if (!labelFields || typeof labelFields !== 'object') return {};
  if (labelFields.fields && typeof labelFields.fields === 'object') {
    return labelFields.fields;
  }
  return labelFields;
}

function isPickupDeliveryType(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
  return [
    'pickup',
    'pickuppoint',
    'pudo',
    'parcelmachine',
    'parcelshop',
    'locker',
  ].includes(normalized);
}

function initialDeliveryType(shipment) {
  if (shipment?.pickup_point_id) return 'pickup_point';
  const fields = labelFieldsPayload(shipment);
  const service = fields.service || {};
  const candidates = [
    service.type,
    service.delivery_type,
    service.deliveryType,
    fields.delivery_type,
    shipment?.service_code,
  ];
  for (const candidate of candidates) {
    if (isPickupDeliveryType(candidate)) return 'pickup_point';
  }
  return 'courier';
}

function normalizeAlpha2Country(value) {
  const text = String(value || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(text) ? text : null;
}

function resolveShipmentCountryCode(shipment, destinationCountryCode = null) {
  const override = normalizeAlpha2Country(destinationCountryCode);
  if (override) return override;

  const fields = labelFieldsPayload(shipment);
  const route = fields.route || {};
  const recipient = fields.recipient || {};
  const address = recipient.address || {};
  const candidates = [
    route.destination,
    address.country_code,
    address.countryCode,
    address.country,
  ];
  for (const candidate of candidates) {
    const text = normalizeAlpha2Country(candidate);
    if (text) return text;
  }
  return null;
}

function pickupPointCountryCode(point) {
  const address = point?.address || {};
  return (
    normalizeAlpha2Country(address.country_code) ||
    normalizeAlpha2Country(address.countryCode) ||
    normalizeAlpha2Country(address.country) ||
    normalizeAlpha2Country(point?.country_code)
  );
}

function filterPickupPointsByCountry(points, countryCode) {
  const expected = normalizeAlpha2Country(countryCode);
  if (!expected) return [];
  return (Array.isArray(points) ? points : []).filter((point) => {
    const pointCountry = pickupPointCountryCode(point);
    // Keep points with unknown address country only when they already matched
    // the DB/API country filter; drop explicit mismatches.
    return !pointCountry || pointCountry === expected;
  });
}

function extractApiErrorMessage(body) {
  const detail = body?.detail;
  if (detail == null) return null;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (typeof detail !== 'object') return String(detail);

  const nested = detail.error;
  if (typeof nested === 'string' && nested.trim()) return nested.trim();
  if (nested && typeof nested === 'object') {
    if (typeof nested.detail === 'string' && nested.detail.trim()) {
      return nested.detail.trim();
    }
    if (nested.detail != null) return JSON.stringify(nested.detail);
  }
  if (typeof detail.message === 'string' && detail.message.trim()) {
    return detail.message.trim();
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return null;
  }
}

function pickupPointOptionLabel(point) {
  const address = point?.address || {};
  const parts = [
    point?.name,
    address.city,
    address.street,
    address.house,
    point?.external_id,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  return parts.join(' — ') || String(point?.external_id || point?.id || '');
}

function buildChangeFormState(shipment) {
  const fields = labelFieldsPayload(shipment);
  const recipient = fields.recipient || {};
  const address = recipient.address || {};
  const deliveryType = initialDeliveryType(shipment);
  const offload =
    address.offload_postcode ||
    address.offloadPostcode ||
    fields.pickup_point_id ||
    '';

  return {
    deliveryType,
    comment: String(fields.shipment_comment || fields.comment || ''),
    name: String(recipient.name || ''),
    mobile: String(recipient.phone || recipient.mobile || ''),
    email: String(recipient.email || ''),
    city: String(address.city || ''),
    street: String(address.street || ''),
    houseNo: String(address.house || address.house_no || ''),
    apartmentNo: String(address.apartment || address.apartment_no || ''),
    postalCode: String(address.postal_code || address.postcode || ''),
    pickupPointId: String(
      offload ||
        (deliveryType === 'pickup_point' && shipment?.pickup_point_id
          ? shipment.pickup_point_id
          : '') ||
        '',
    ),
  };
}

function ShipmentChangeModal({
  shipment,
  shipmentId,
  theme,
  environment,
  user,
  destinationCountryCode,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => buildChangeFormState(shipment));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [pickupPointsLoading, setPickupPointsLoading] = useState(false);
  const [pickupPointsError, setPickupPointsError] = useState(null);

  const isPickup = form.deliveryType === 'pickup_point';
  const shipmentCountryCode = resolveShipmentCountryCode(shipment, destinationCountryCode);
  const canSubmit =
    Boolean(shipmentId) &&
    !submitting &&
    Boolean(String(form.comment || '').trim()) &&
    Boolean(String(form.city || '').trim()) &&
    Boolean(String(form.street || '').trim()) &&
    (!isPickup || Boolean(String(form.pickupPointId || '').trim()));

  useEffect(() => {
    if (!isPickup) {
      setPickupPoints([]);
      setPickupPointsError(null);
      setPickupPointsLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadPickupPoints() {
      const token = localStorage.getItem('token');
      if (!token) {
        setPickupPoints([]);
        setPickupPointsError('ordersMissingToken');
        setPickupPointsLoading(false);
        return;
      }
      if (!shipmentCountryCode) {
        setPickupPoints([]);
        setPickupPointsError('shipmentEditorEditCountryRequired');
        setPickupPointsLoading(false);
        return;
      }

      const params = new URLSearchParams({
        provider_code: 'OMNIVA',
        only_active: 'true',
        environment: environment || 'TEST',
        country_code: shipmentCountryCode,
      });

      try {
        setPickupPointsLoading(true);
        setPickupPointsError(null);
        const res = await fetch(deliveryCoreUrl(`/v1/pickup-points?${params.toString()}`), {
          headers: ordersRequestHeaders(token, user),
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (cancelled) return;
        const points = filterPickupPointsByCountry(
          Array.isArray(data) ? data : [],
          shipmentCountryCode,
        );
        const selected = String(form.pickupPointId || '').trim();
        const selectedInCountry = selected
          ? points.find((point) => String(point?.external_id || '').trim() === selected)
          : null;
        // Do not inject out-of-country pickup points; country cannot change.
        if (selected && !selectedInCountry) {
          setForm((current) => ({
            ...current,
            pickupPointId: '',
          }));
        }
        setPickupPoints(points);
        if (selectedInCountry) {
          const address = selectedInCountry.address || {};
          if (address.city || address.street) {
            setForm((current) => ({
              ...current,
              city: String(address.city || current.city || ''),
              street: String(address.street || current.street || ''),
              houseNo: String(address.house || address.house_no || current.houseNo || ''),
              apartmentNo: String(
                address.apartment || address.apartment_no || current.apartmentNo || '',
              ),
              postalCode: String(
                address.postal_code || address.postcode || current.postalCode || '',
              ),
            }));
          }
        }
      } catch (err) {
        console.error('Failed to load pickup points:', err);
        if (!cancelled) {
          setPickupPoints([]);
          setPickupPointsError('shipmentEditorEditPickupPointsError');
        }
      } finally {
        if (!cancelled) setPickupPointsLoading(false);
      }
    }

    loadPickupPoints();
    return () => {
      cancelled = true;
    };
  }, [isPickup, environment, shipmentCountryCode, user]);

  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function applyPickupPointAddress(externalId) {
    const selected = String(externalId || '').trim();
    const point = pickupPoints.find(
      (item) => String(item?.external_id || '').trim() === selected,
    );
    const address = point?.address || {};
    setForm((current) => ({
      ...current,
      pickupPointId: selected,
      city: String(address.city || current.city || ''),
      street: String(address.street || current.street || ''),
      houseNo: String(address.house || address.house_no || current.houseNo || ''),
      apartmentNo: String(
        address.apartment || address.apartment_no || current.apartmentNo || '',
      ),
      postalCode: String(
        address.postal_code || address.postcode || current.postalCode || '',
      ),
    }));
  }

  async function handleSave() {
    if (!canSubmit) return;

    if (isPickup && !String(form.pickupPointId || '').trim()) {
      setError(t('shipmentEditorEditPickupPointRequired'));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError(t('ordersMissingToken'));
      return;
    }

    const body = {
      shipment_id: shipmentId,
      delivery_type: form.deliveryType,
      environment: environment || 'TEST',
      comment: form.comment || '',
      receiver_data: {
        name: form.name || null,
        mobile: form.mobile || null,
        email: form.email || null,
        city: String(form.city || '').trim(),
        street: String(form.street || '').trim(),
        house_no: form.houseNo || null,
        apartment_no: form.apartmentNo || null,
        postal_code: isPickup ? null : form.postalCode || null,
      },
      pickup_point_id: isPickup ? String(form.pickupPointId || '').trim() : null,
    };

    try {
      setSubmitting(true);
      setError(null);
      const res = await fetch(
        deliveryCoreUrl(`/v1/shipments/${encodeURIComponent(shipmentId)}/change`),
        {
          method: 'POST',
          headers: {
            ...ordersRequestHeaders(token, user),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        let message = t('shipmentEditorEditError');
        try {
          const payload = await res.json();
          message = extractApiErrorMessage(payload) || message;
        } catch {
          /* ignore non-JSON error bodies */
        }
        setError(message);
        return;
      }
      let savedPayload = null;
      try {
        savedPayload = await res.json();
      } catch {
        savedPayload = null;
      }
      if (typeof onSaved === 'function') {
        onSaved(savedPayload);
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Failed to change shipment:', err);
      setError(t('shipmentEditorEditError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={['webhooks-modalOverlay', 'webhooks-modalOverlay--nested', theme].join(' ')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="webhooks-modal">
        <div className="webhooks-modalHeader">
          <div className="webhooks-modalTitle">
            {t('shipmentEditorEditTitle', { id: shipmentId || '—' })}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={['webhooks-iconBtn', theme].join(' ')}
            aria-label={t('Cancel')}
            title={t('Cancel')}
            disabled={submitting}
          >
            ×
          </button>
        </div>

        <div className="webhooks-modalBody">
          <div className="webhooks-shipmentChangeForm">
            <div className="webhooks-label">
              <div className="webhooks-fieldLabel">{t('shipmentEditorEditDeliveryType')}</div>
              <div
                className="webhooks-segmentSwitch"
                role="group"
                aria-label={t('shipmentEditorEditDeliveryType')}
              >
                <button
                  type="button"
                  className={[
                    'webhooks-segmentSwitchBtn',
                    form.deliveryType === 'courier' ? 'is-active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => updateField('deliveryType', 'courier')}
                  disabled={submitting}
                >
                  {t('shipmentEditorEditCourier')}
                </button>
                <button
                  type="button"
                  className={[
                    'webhooks-segmentSwitchBtn',
                    form.deliveryType === 'pickup_point' ? 'is-active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => updateField('deliveryType', 'pickup_point')}
                  disabled={submitting}
                >
                  {t('shipmentEditorEditPickupPoint')}
                </button>
              </div>
            </div>

            <div className="webhooks-formGrid">
              <div className="webhooks-label">
                <div className="webhooks-fieldLabel">{t('shipmentEditorEditReceiverName')}</div>
                <input
                  className={['webhooks-input', theme].join(' ')}
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="webhooks-label">
                <div className="webhooks-fieldLabel">{t('shipmentEditorEditReceiverMobile')}</div>
                <input
                  className={['webhooks-input', theme].join(' ')}
                  value={form.mobile}
                  onChange={(e) => updateField('mobile', e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="webhooks-label">
                <div className="webhooks-fieldLabel">{t('shipmentEditorEditReceiverEmail')}</div>
                <input
                  className={['webhooks-input', theme].join(' ')}
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  disabled={submitting}
                />
              </div>

              {isPickup && (
                <div className="webhooks-label" style={{ gridColumn: '1 / -1' }}>
                  <div className="webhooks-fieldLabel">{t('shipmentEditorEditPickupPointId')}</div>
                  <select
                    className={['webhooks-select', theme].join(' ')}
                    value={form.pickupPointId}
                    onChange={(e) => applyPickupPointAddress(e.target.value)}
                    disabled={submitting || pickupPointsLoading}
                  >
                    <option value="">
                      {pickupPointsLoading
                        ? t('shipmentEditorEditPickupPointsLoading')
                        : t('shipmentEditorEditPickupPointSelect')}
                    </option>
                    {pickupPoints.map((point) => {
                      const value = String(point?.external_id || '').trim();
                      if (!value) return null;
                      return (
                        <option key={point?.id || value} value={value}>
                          {pickupPointOptionLabel(point)}
                        </option>
                      );
                    })}
                  </select>
                  {pickupPointsError && (
                    <div className={['webhooks-error', theme].join(' ')} style={{ marginTop: 8 }}>
                      {t(pickupPointsError)}
                    </div>
                  )}
                </div>
              )}

              <div className="webhooks-label">
                <div className="webhooks-fieldLabel">{t('shipmentEditorEditCity')}</div>
                <input
                  className={['webhooks-input', theme].join(' ')}
                  value={form.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  disabled={submitting || isPickup}
                />
              </div>
              <div className="webhooks-label">
                <div className="webhooks-fieldLabel">{t('shipmentEditorEditStreet')}</div>
                <input
                  className={['webhooks-input', theme].join(' ')}
                  value={form.street}
                  onChange={(e) => updateField('street', e.target.value)}
                  disabled={submitting || isPickup}
                />
              </div>

              {!isPickup && (
                <>
                  <div className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('shipmentEditorEditHouseNo')}</div>
                    <input
                      className={['webhooks-input', theme].join(' ')}
                      value={form.houseNo}
                      onChange={(e) => updateField('houseNo', e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                  <div className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('shipmentEditorEditApartmentNo')}</div>
                    <input
                      className={['webhooks-input', theme].join(' ')}
                      value={form.apartmentNo}
                      onChange={(e) => updateField('apartmentNo', e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                  <div className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('shipmentEditorEditPostalCode')}</div>
                    <input
                      className={['webhooks-input', theme].join(' ')}
                      value={form.postalCode}
                      onChange={(e) => updateField('postalCode', e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                </>
              )}

              <div className="webhooks-label" style={{ gridColumn: '1 / -1' }}>
                <div className="webhooks-fieldLabel">{t('shipmentEditorEditComment')}</div>
                <textarea
                  className={['webhooks-textarea', theme].join(' ')}
                  rows={3}
                  value={form.comment}
                  onChange={(e) => updateField('comment', e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          {error && (
            <div className={['webhooks-error', theme].join(' ')} style={{ marginTop: 12 }}>
              {error}
            </div>
          )}
        </div>

        <div className="webhooks-modalFooter">
          <button
            type="button"
            className={[
              'webhooks-primaryBtn',
              theme,
              canSubmit ? 'enabled' : 'disabled',
            ].join(' ')}
            onClick={handleSave}
            disabled={!canSubmit}
          >
            {submitting ? t('shipmentEditorEditSaving') : t('shipmentEditorEditSave')}
          </button>
          <button
            type="button"
            className={[
              'webhooks-secondaryBtn',
              theme,
              submitting ? 'disabled' : 'enabled',
            ].join(' ')}
            onClick={onClose}
            disabled={submitting}
          >
            {t('Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ShipmentEditor({
  shipment,
  theme,
  destinationCountryCode,
  onClose,
  onDeleted,
  onChanged,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { environment } = useEnvironment();
  const spinnerColor = theme === 'dark' ? 'white' : 'black';

  const [labelImages, setLabelImages] = useState([]);
  const [labelImagesLoading, setLabelImagesLoading] = useState(false);
  const [labelImagesError, setLabelImagesError] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadLabelImages() {
      const shipmentId = shipment?.id;
      if (!shipmentId) {
        setLabelImages([]);
        setLabelImagesError(null);
        setLabelImagesLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setLabelImages([]);
        setLabelImagesError('ordersMissingToken');
        setLabelImagesLoading(false);
        return;
      }

      try {
        setLabelImagesLoading(true);
        setLabelImagesError(null);
        const res = await fetch(
          deliveryCoreUrl(`/v1/label/images/${encodeURIComponent(shipmentId)}`),
          {
            headers: ordersRequestHeaders(token, user),
          },
        );

        if (res.status === 404) {
          if (!cancelled) setLabelImages([]);
          return;
        }

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        if (!cancelled) {
          setLabelImages(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Failed to load label images:', err);
        if (!cancelled) {
          setLabelImages([]);
          setLabelImagesError('shipmentEditorLabelImagesError');
        }
      } finally {
        if (!cancelled) setLabelImagesLoading(false);
      }
    }

    loadLabelImages();
    return () => {
      cancelled = true;
    };
  }, [shipment?.id, user, refreshKey]);

  if (!shipment) {
    return null;
  }

  const routing = shipment.routing || {};
  const trackingNumbers = Array.isArray(shipment.tracking_numbers)
    ? shipment.tracking_numbers.filter(Boolean)
    : (shipment.tracking_number ? [shipment.tracking_number] : []);
  const trackingDisplay = trackingNumbers.join(', ');
  const titleId = trackingDisplay || shipment.carrier_order_number || shipment.id || '—';
  const shipmentId = String(shipment.id || '').trim();
  const providerCode = String(shipment.provider_code || '').trim().toUpperCase();
  const shipmentStatus = String(shipment.status || '').trim().toLowerCase();
  const canEdit = providerCode === 'OMNIVA' && shipmentStatus === 'created';
  const canDelete = Boolean(shipmentId && providerCode) && !deleteSubmitting;

  async function handleDeleteShipment() {
    if (!canDelete) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setDeleteError('ordersMissingToken');
      return;
    }

    try {
      setDeleteSubmitting(true);
      setDeleteError(null);
      const res = await fetch(deliveryCoreUrl('/v1/shipments'), {
        method: 'DELETE',
        headers: {
          ...ordersRequestHeaders(token, user),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: [shipmentId],
          provider_code: providerCode,
          environment: environment || 'TEST',
        }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      if (typeof onDeleted === 'function') {
        onDeleted();
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Failed to delete shipment:', err);
      setDeleteError('shipmentEditorDeleteError');
    } finally {
      setDeleteSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={['webhooks-modalOverlay', 'webhooks-modalOverlay--nested', theme].join(' ')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="webhooks-modal webhooks-modal--orderDetails">
        <div className="webhooks-modalHeader">
          <div className="webhooks-modalTitle">
            {t('shipmentEditorTitle', { id: titleId })}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={['webhooks-iconBtn', theme].join(' ')}
            aria-label={t('Cancel')}
            title={t('Cancel')}
          >
            ×
          </button>
        </div>

        <div className="webhooks-modalBody">
          <div className="webhooks-shipmentEditorSplit">
            <div className="webhooks-shipmentEditorFields">
              <div className="webhooks-formGrid">
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('ordersDetailsShipmentId')}</div>
                  <FieldValue>{String(shipment.id || '—')}</FieldValue>
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('ordersDetailsShipmentStatus')}</div>
                  <FieldValue>{String(shipment.status || '—')}</FieldValue>
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('ordersDetailsShipmentProvider')}</div>
                  <FieldValue>{String(shipment.provider_code || '—').toUpperCase()}</FieldValue>
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('ordersDetailsCarrierOrderNumber')}</div>
                  <FieldValue>{String(shipment.carrier_order_number || '—')}</FieldValue>
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('ordersDetailsShipmentTracking')}</div>
                  {trackingDisplay ? (
                    <button
                      type="button"
                      className={['webhooks-idBtn', 'webhooks-idBtn--full', theme].join(' ')}
                      title={trackingDisplay}
                      onClick={async () => {
                        const ok = await copyTextToClipboard(trackingDisplay);
                        if (ok) {
                          alert(t('copiedToClipboard'));
                        }
                      }}
                    >
                      <span className="webhooks-idText webhooks-idText--full">{trackingDisplay}</span>
                    </button>
                  ) : (
                    <FieldValue>—</FieldValue>
                  )}
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('ordersDetailsShipmentServiceCode')}</div>
                  <FieldValue>{String(shipment.service_code || '—')}</FieldValue>
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('ordersDetailsShipmentCreated')}</div>
                  <FieldValue>{formatTs(shipment.created_at)}</FieldValue>
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('ordersDetailsShipmentUpdated')}</div>
                  <FieldValue>{formatTs(shipment.updated_at)}</FieldValue>
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('ordersDetailsShipmentPricingRule')}</div>
                  <FieldValue>{String(shipment.pricing_rule_id || '—')}</FieldValue>
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('shipmentEditorRoutingOrigin')}</div>
                  <MultilineText value={routing.origin} />
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('shipmentEditorRoutingDestination')}</div>
                  <MultilineText value={routing.destination} />
                </div>
                <div className="webhooks-label">
                  <div className="webhooks-fieldLabel">{t('shipmentEditorRoutingReferences')}</div>
                  <FieldValue>{formatReferences(routing.references)}</FieldValue>
                </div>
              </div>

              <div className="webhooks-shipmentEditorBoxes">
                <div className="webhooks-fieldLabel">{t('shipmentEditorBoxes')}</div>
                <ShipmentBoxesTable shipment={shipment} t={t} />
              </div>
            </div>

            <div className="webhooks-shipmentEditorLabels">
              <div className="webhooks-fieldLabel">{t('shipmentEditorLabelImages')}</div>
              {labelImagesLoading && (
                <div className="webhooks-labelImagesStatus">
                  <ClipLoader size={18} color={spinnerColor} />
                  <span>{t('shipmentEditorLabelImagesLoading')}</span>
                </div>
              )}
              {!labelImagesLoading && labelImagesError && (
                <div className={['webhooks-error', theme].join(' ')}>{t(labelImagesError)}</div>
              )}
              {!labelImagesLoading && !labelImagesError && labelImages.length === 0 && (
                <div className="webhooks-muted">{t('shipmentEditorLabelImagesNone')}</div>
              )}
              {!labelImagesLoading && !labelImagesError && labelImages.length > 0 && (
                <LabelImageCarousel images={labelImages} theme={theme} t={t} />
              )}
            </div>
          </div>
        </div>

        {deleteError && (
          <div className={['webhooks-error', theme].join(' ')} style={{ marginTop: 10 }}>
            {t(deleteError)}
          </div>
        )}

        <div className="webhooks-modalFooter">
          {canEdit && (
            <button
              type="button"
              className={[
                'webhooks-secondaryBtn',
                theme,
                deleteSubmitting ? 'disabled' : 'enabled',
              ].join(' ')}
              onClick={() => setEditOpen(true)}
              disabled={deleteSubmitting}
            >
              <span className="orders-createShipmentBtnContent">
                <span className="oi oi-pencil" aria-hidden="true" />
                {t('shipmentEditorEdit')}
              </span>
            </button>
          )}
          <button
            type="button"
            className={[
              'webhooks-dangerBtn',
              theme,
              canDelete ? 'enabled' : 'disabled',
            ].join(' ')}
            onClick={handleDeleteShipment}
            disabled={!canDelete}
          >
            <span className="orders-createShipmentBtnContent">
              <span className="oi oi-warning" aria-hidden="true" />
              {deleteSubmitting ? t('Deleting') : t('shipmentEditorDelete')}
            </span>
          </button>
          <button
            type="button"
            className={[
              'webhooks-secondaryBtn',
              theme,
              deleteSubmitting ? 'disabled' : 'enabled',
            ].join(' ')}
            onClick={onClose}
            disabled={deleteSubmitting}
          >
            {t('Cancel')}
          </button>
        </div>
      </div>

      {editOpen && (
        <ShipmentChangeModal
          shipment={shipment}
          shipmentId={shipmentId}
          theme={theme}
          environment={environment}
          user={user}
          destinationCountryCode={destinationCountryCode}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            setRefreshKey((key) => key + 1);
            if (typeof onChanged === 'function') {
              onChanged();
            }
          }}
        />
      )}
    </div>
  );
}
